import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Node, NodeComponentProps, useZoomPanHelper} from 'react-flow-renderer';
import {FadeInOutView, Tooltip, useTheme} from '@innowise-group/ui-kit';
import {
  Can,
  RequestOnPlan,
  RoomType,
  Workspace,
  WorkspaceStatuses,
  useService,
  WorkspacesApiService,
} from '@innowise-group/core';
import {getThemeByRequestType} from '@innowise-group/utilities';
import {useLocation} from 'react-router';
import {useTranslation} from 'react-i18next';

import * as Styled from './workspace-view.styles';
import WorkspaceActions from './workspace-actions.component';
import WorkspaceInfo from './workspace-info.component';
import {FloorContext, ShowOnPlanRequest} from '../floor-plan/floor-plan.component';
import {useSearchParams} from 'react-router-dom';
import {
  coordinatesToTopLeft,
  findCollidingWorkspace,
  getAttendanceBadgeColor,
  checkWorkspaceRoomBounds,
  proceedAttendanceBadgeVisibility,
  getBookedStatusEmployeeName,
  getBookedStatusesForTooltip,
  getActiveRequestsForTooltip,
} from '../../utilities';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';

export interface WorkspaceElementData extends Workspace {
  isViewMode: boolean;
  isSaving?: boolean;
  isDraft?: boolean;
  requestData?: RequestOnPlan;
}

const WorkspaceView: React.FC<NodeComponentProps<WorkspaceElementData>> = ({data, selected}) => {
  const {
    id,
    type,
    coordinates,
    employee,
    status,
    room,
    comment,
    range,
    isViewMode,
    number,
    height,
    width,
    bookedStatus,
    activeRequests,
    rotateDegree,
    isSaving: isSavingFromData,
    isDraft,
  } = data;
  const [isLocalSaving, setIsLocalSaving] = useState(false);
  const isSaving = Boolean(isSavingFromData || isLocalSaving);
  const workspace = useMemo(() => {
    return {
      id,
      number,
      room,
      comment,
      range,
      coordinates,
      type,
      employee,
      status,
      bookedStatus,
      rotateDegree,
      width,
      height,
      activeRequests,
      isDraft,
    };
  }, [
    bookedStatus,
    comment,
    coordinates,
    employee,
    height,
    id,
    number,
    range,
    room,
    rotateDegree,
    status,
    type,
    width,
    activeRequests,
    isDraft,
  ]);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const {setCenter} = useZoomPanHelper();
  const {floorsEventEmitter, floor, handleAddNewWorkspace, workspaceElements, roomElements, bookingFilterRange} =
    useContext(FloorContext);
  const workspacesApi = useService(WorkspacesApiService);
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const {t} = useTranslation();
  const {focusWorkspace, ...requestData} = useContext(ShowOnPlanRequest);
  const {state} = useLocation() as {state: {workspaceId: string; focusTimestamp?: number}};
  const [searchParams] = useSearchParams();

  const focusOnTable = useCallback(() => {
    const x = coordinates[0][0] + width / 2;
    const y = coordinates[0][1] + height / 2;
    const zoom = 1.05;
    setCenter(x, y, zoom, 800);
  }, [coordinates, width, height, setCenter]);

  const foundedWorkspace = useMemo(() => {
    return (
      state?.workspaceId === id ||
      requestData?.idWorkspace === id ||
      focusWorkspace?.id === id ||
      searchParams.get('focus_on') === id
    );
  }, [state?.workspaceId, id, requestData, focusWorkspace, searchParams]);

  const theme = useTheme();
  const color = getThemeByRequestType(status, theme);
  const attendanceBadgeColor = getAttendanceBadgeColor(employee?.visitationPercent, theme);
  const isAttendanceBadgeVisible = proceedAttendanceBadgeVisibility(employee?.visitationPercent, status);

  useEffect(() => {
    if (isDraft) {
      return;
    }
    const shouldFocus = foundedWorkspace || (state?.workspaceId === id && state?.focusTimestamp);

    if (workspaceRef.current && shouldFocus) {
      const time = setTimeout(() => {
        focusOnTable();
        workspaceRef.current?.click();
      }, 0);
      return () => clearTimeout(time);
    }
  }, [workspaceRef, foundedWorkspace, state?.focusTimestamp, state?.workspaceId, id, focusOnTable, isDraft]);

  const handleEditClick = useCallback(() => {
    floorsEventEmitter.emit('editWorkspace', workspace);
  }, [floorsEventEmitter, workspace]);

  const canRotateTo = useCallback(
    (nextRotateDegree: number) => {
      const {x, y} = coordinatesToTopLeft(coordinates, width, height);
      const candidate = {
        id: `${type}-${id}`,
        x,
        y,
        width,
        height,
        rotateDegree: nextRotateDegree,
      };

      const collidedWorkspace = findCollidingWorkspace(
        candidate,
        (workspaceElements || []).map(({id: elementId, position, data}: Node<WorkspaceElementData>) => ({
          id: elementId,
          x: position.x,
          y: position.y,
          width: data.width,
          height: data.height,
          rotateDegree: data.rotateDegree,
          number: data.number,
        })),
      );

      if (collidedWorkspace) {
        floorsModalsFacade.openInfoModal(
          t('modals.workspacesIntersectionMessage', {number: collidedWorkspace.number}),
          t('modals.attention'),
          t('buttons.ok'),
        );
        return false;
      }

      if (!floor?.isVirtual) {
        const roomBoundsResult = checkWorkspaceRoomBounds(
          candidate,
          (roomElements || []).map(({data}) => ({id: data.id, polygon: data.polygon})),
        );
        if (roomBoundsResult !== 'ok') {
          const messageKey =
            roomBoundsResult === 'outsideRoom' ? 'modals.outsideRoomMessage' : 'modals.withinRoomMessage';
          floorsModalsFacade.openInfoModal(t(messageKey), t('modals.attention'), t('buttons.ok'));
          return false;
        }
      }

      return true;
    },
    [coordinates, floorsModalsFacade, floor?.isVirtual, height, id, roomElements, t, type, width, workspaceElements],
  );

  const refreshWorkspaceAfterMutation = useCallback(() => {
    workspacesApi.getWorkspaceResponseById(String(id)).subscribe({
      next: (workspace) => {
        floorsEventEmitter.emit('applyWorkspaceUpdate', workspace);
        setIsLocalSaving(false);
      },
      error: () => setIsLocalSaving(false),
    });
  }, [floorsEventEmitter, id, workspacesApi]);

  const handleClockwiseRotateClick = useCallback(() => {
    const nextRotateDegree = rotateDegree + 15;
    if (!canRotateTo(nextRotateDegree)) {
      return;
    }

    setIsLocalSaving(true);
    workspacesApi.rotateWorkspace(id, nextRotateDegree, status).subscribe({
      next: () => refreshWorkspaceAfterMutation(),
      error: () => setIsLocalSaving(false),
    });
  }, [canRotateTo, refreshWorkspaceAfterMutation, id, rotateDegree, status, workspacesApi]);

  const handleAntiClockwiseRotateClick = useCallback(() => {
    const nextRotateDegree = rotateDegree - 15;
    if (!canRotateTo(nextRotateDegree)) {
      return;
    }

    setIsLocalSaving(true);
    workspacesApi.rotateWorkspace(id, nextRotateDegree, status).subscribe({
      next: () => refreshWorkspaceAfterMutation(),
      error: () => setIsLocalSaving(false),
    });
  }, [canRotateTo, refreshWorkspaceAfterMutation, id, rotateDegree, status, workspacesApi]);

  const handleDeleteClick = useCallback(() => {
    floorsEventEmitter.emit('deleteWorkspace', workspace);
  }, [floorsEventEmitter, workspace]);

  const handleBookWorkspaceClick = useCallback(() => {
    floorsEventEmitter.emit('bookWorkspace', id, room.id, bookedStatus);
  }, [bookedStatus, floorsEventEmitter, id, room]);

  const isVirtualFloor = useMemo(() => {
    return status === WorkspaceStatuses.Remote;
  }, [status]);

  const handleCopyWorkspaceClick = useCallback(() => {
    handleAddNewWorkspace(isVirtualFloor, rotateDegree);
  }, [handleAddNewWorkspace, isVirtualFloor, rotateDegree]);

  const copyWorkspaceIcon = useMemo(() => {
    return (
      status === WorkspaceStatuses.Free ||
      status === WorkspaceStatuses.CoworkingFree ||
      (status === WorkspaceStatuses.Remote && !employee?.id)
    );
  }, [status, employee?.id]);

  const validateRoomWithoutBookingAccess = (roomType: RoomType) => {
    return roomType === 'Water closet' || roomType === 'Hall';
  };

  const getEmployeeName = () => {
    if (status === WorkspaceStatuses.Free || status === WorkspaceStatuses.CoworkingFree) return '';
    if (bookedStatus?.length > 0) {
      return getBookedStatusEmployeeName(bookedStatus, bookingFilterRange);
    }
    if (employee?.fullName) {
      return employee?.fullName;
    }
  };

  const isEmployeeNameShown = !!getEmployeeName();

  const tooltipBookedStatus = useMemo(
    () => getBookedStatusesForTooltip(bookedStatus, bookingFilterRange),
    [bookedStatus, bookingFilterRange],
  );

  const tooltipActiveRequests = useMemo(
    () => getActiveRequestsForTooltip(activeRequests, bookingFilterRange),
    [activeRequests, bookingFilterRange],
  );

  const workspaceNode = (
    <Styled.WorkspaceViewContainer
      ref={workspaceRef}
      selected={selected}
      themeColor={activeRequests.length && status === WorkspaceStatuses.Free ? theme.palette.main : color}
      rotateDegree={rotateDegree}
      isViewMode={isViewMode}
      isDraft={isDraft}
      className={type}
      data-testid={`workspace-${id}`}
      data-employee-label={isEmployeeNameShown ? getEmployeeName() || '' : ''}
    >
      {!isDraft && (
        <Can I="READ" a="GEOTRACK">
          {isAttendanceBadgeVisible && (
            <Styled.AttendanceBadge backgroundColor={attendanceBadgeColor}>
              <p>
                {Math.round(employee?.visitationPercent)}
                <span>%</span>
              </p>
            </Styled.AttendanceBadge>
          )}
        </Can>
      )}
      <Styled.Wrapper>
        {isSaving ? (
          <Styled.SavingSpinner />
        ) : (
          !isDraft && (
            <>
              <Styled.Title>{number}</Styled.Title>
              {isEmployeeNameShown && <Styled.Subtitle>{getEmployeeName()}</Styled.Subtitle>}
            </>
          )
        )}
      </Styled.Wrapper>
    </Styled.WorkspaceViewContainer>
  );

  if (isDraft) {
    return workspaceNode;
  }

  return (
    <Tooltip
      vertical="top"
      horizontal="center"
      active={selected}
      disableAutoPositioning={true}
      content={
        <FadeInOutView>
          {isViewMode ? (
            <WorkspaceInfo
              status={status}
              employee={employee}
              color={color.default}
              range={range}
              bookedStatus={tooltipBookedStatus}
              activeRequests={tooltipActiveRequests}
              onBookWorkspaceClick={handleBookWorkspaceClick}
              showOnPlan={
                requestData?.showOnPlan || !!focusWorkspace || validateRoomWithoutBookingAccess(room.room_type)
              }
              comment={comment}
              floor={floor}
            />
          ) : (
            <WorkspaceActions
              onAntiClockwiseRotateClick={handleAntiClockwiseRotateClick}
              onClockwiseRotateClick={handleClockwiseRotateClick}
              onDeleteClick={handleDeleteClick}
              onEditClick={handleEditClick}
              onCopyWorkspaceClick={handleCopyWorkspaceClick}
              status={status}
              workspace={workspace}
              copyWorkspaceIcon={copyWorkspaceIcon}
            />
          )}
        </FadeInOutView>
      }
    >
      {workspaceNode}
    </Tooltip>
  );
};

export default React.memo(WorkspaceView);
